import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  APP_BGM_PREFERENCE_SYNC_EVENT,
  getAppBgmEnabled,
  initAppBgmPreference,
  setAppBgmEnabledLocal,
} from '../audio/appBgmPreference';

/** Manifest at `public/assets/sounds/`; audio files live in `public/assets/sounds/background/`. */
const MANIFEST_URL = `${(process.env.PUBLIC_URL || '').replace(/\/$/, '')}/assets/sounds/background-music-manifest.json`;

/** Após autoplay em `muted`, tenta ativar o som sozinho (vários browsers ainda exigem um gesto). */
const AUTO_UNMUTE_DELAY_MS = 2000;

type ManifestShape = { tracks?: string[] };

export type AppBackgroundMusicContextValue = {
  /** True enquanto a faixa está a tocar (pode estar muted até o desmute automático ou um gesto). */
  isPlaying: boolean;
  /** Manifest loaded with at least one valid track URL. */
  isAvailable: boolean;
  toggle: () => void;
};

const AppBackgroundMusicContext = createContext<AppBackgroundMusicContextValue | null>(null);

/** Paths in the manifest are relative to `public/assets/sounds/background/`. */
function resolveTrackUrl(relative: string): string {
  const publicBase = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  let rel = relative.trim().replace(/^\/+/, '');
  const bgPrefix = 'background/';
  if (rel.startsWith(bgPrefix)) {
    rel = rel.slice(bgPrefix.length);
  }
  const encodedPath = rel
    .split('/')
    .filter(Boolean)
    .map((seg) => encodeURIComponent(seg))
    .join('/');
  return `${publicBase}/assets/sounds/background/${encodedPath}`;
}

function pickNextUrl(urls: string[], current: string | null): string {
  if (urls.length === 0) return '';
  if (urls.length === 1) return urls[0];
  const others = current ? urls.filter((u) => u !== current) : urls;
  const pool = others.length > 0 ? others : urls;
  return pool[Math.floor(Math.random() * pool.length)];
}

export const AppBackgroundMusicProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const urlsRef = useRef<string[]>([]);
  const currentUrlRef = useRef<string | null>(null);
  const errorSkipRef = useRef(0);
  const bgmEnabledRef = useRef(getAppBgmEnabled());
  const tryStartIfAllowedRef = useRef<() => void>(() => {});
  const attemptPlayRef = useRef<(media: HTMLAudioElement, allowMutedFallback: boolean) => Promise<void>>(
    async () => {}
  );
  /** True quando o autoplay começou em silêncio e ainda não desmutámos (timer ou gesto). */
  const pendingGestureUnmuteRef = useRef(false);
  const autoUnmuteTimerRef = useRef<number | null>(null);

  const clearAutoUnmuteTimer = useCallback(() => {
    if (autoUnmuteTimerRef.current !== null) {
      clearTimeout(autoUnmuteTimerRef.current);
      autoUnmuteTimerRef.current = null;
    }
  }, []);

  const [bgmEnabled, setBgmEnabled] = useState(() => getAppBgmEnabled());
  const [isPlaying, setIsPlaying] = useState(false);
  const [isAvailable, setIsAvailable] = useState(false);

  bgmEnabledRef.current = bgmEnabled;

  const attemptPlay = useCallback(
    async (media: HTMLAudioElement, allowMutedFallback: boolean) => {
      if (!bgmEnabledRef.current) {
        setIsPlaying(false);
        return;
      }
      media.muted = false;
      try {
        await media.play();
        pendingGestureUnmuteRef.current = false;
        clearAutoUnmuteTimer();
        setIsPlaying(true);
        return;
      } catch {
        /* blocked autoplay or transient error */
      }
      if (!allowMutedFallback) {
        setIsPlaying(false);
        return;
      }
      try {
        clearAutoUnmuteTimer();
        media.muted = true;
        await media.play();
        setIsPlaying(true);
        pendingGestureUnmuteRef.current = true;
        autoUnmuteTimerRef.current = window.setTimeout(() => {
          autoUnmuteTimerRef.current = null;
          if (!pendingGestureUnmuteRef.current || !bgmEnabledRef.current) return;
          const el = audioRef.current;
          if (!el) return;
          try {
            el.muted = false;
            pendingGestureUnmuteRef.current = false;
          } catch {
            /* ignore */
          }
        }, AUTO_UNMUTE_DELAY_MS);
      } catch {
        setIsPlaying(false);
      }
    },
    [clearAutoUnmuteTimer]
  );

  attemptPlayRef.current = attemptPlay;

  const ensureAudio = useCallback(() => {
    if (typeof window === 'undefined') return null;
    if (!audioRef.current) {
      const a = new Audio();
      a.preload = 'auto';
      a.volume = 0.28;
      a.setAttribute('playsinline', 'true');
      a.setAttribute('webkit-playsinline', 'true');
      const playUrl = (url: string) => {
        currentUrlRef.current = url;
        a.src = url;
        a.load();
        void attemptPlayRef.current(a, true);
      };
      a.addEventListener('ended', () => {
        const list = urlsRef.current;
        if (list.length === 0) return;
        errorSkipRef.current = 0;
        const next = pickNextUrl(list, currentUrlRef.current);
        playUrl(next);
      });
      a.addEventListener('error', () => {
        const list = urlsRef.current;
        if (list.length === 0) {
          setIsPlaying(false);
          return;
        }
        errorSkipRef.current += 1;
        if (errorSkipRef.current > list.length * 2) {
          setIsPlaying(false);
          return;
        }
        const next = pickNextUrl(list, currentUrlRef.current);
        playUrl(next);
      });
      a.addEventListener('play', () => {
        errorSkipRef.current = 0;
        setIsPlaying(true);
      });
      a.addEventListener('pause', () => setIsPlaying(false));
      audioRef.current = a;
    }
    return audioRef.current;
  }, []);

  const playRandom = useCallback(() => {
    const list = urlsRef.current;
    if (list.length === 0) return;
    const a = ensureAudio();
    if (!a) return;
    const next = pickNextUrl(list, currentUrlRef.current);
    currentUrlRef.current = next;
    a.src = next;
    a.load();
    void attemptPlayRef.current(a, true);
  }, [ensureAudio]);

  const tryStartIfAllowed = useCallback(() => {
    if (!bgmEnabledRef.current || urlsRef.current.length === 0) return;
    const a = audioRef.current;
    if (!a || !a.src) {
      playRandom();
      return;
    }
    void attemptPlayRef.current(a, true);
  }, [playRandom]);

  tryStartIfAllowedRef.current = tryStartIfAllowed;

  useEffect(() => {
    initAppBgmPreference();
    setBgmEnabled(getAppBgmEnabled());
  }, []);

  useEffect(() => {
    const onSync = () => {
      setBgmEnabled(getAppBgmEnabled());
    };
    window.addEventListener(APP_BGM_PREFERENCE_SYNC_EVENT, onSync);
    return () => window.removeEventListener(APP_BGM_PREFERENCE_SYNC_EVENT, onSync);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let kickTimer1: ReturnType<typeof setTimeout> | undefined;
    let kickTimer2: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        const res = await fetch(MANIFEST_URL, { cache: 'no-store' });
        if (!res.ok) return;
        const data = (await res.json()) as ManifestShape;
        const raw = Array.isArray(data.tracks) ? data.tracks : [];
        const urls = raw
          .map((t) => (typeof t === 'string' ? resolveTrackUrl(t) : ''))
          .filter(Boolean);
        if (cancelled) return;
        urlsRef.current = urls;
        setIsAvailable(urls.length > 0);
        if (urls.length > 0) {
          ensureAudio();
          if (getAppBgmEnabled()) {
            const kick = () => {
              if (cancelled || !bgmEnabledRef.current) return;
              tryStartIfAllowedRef.current();
            };
            queueMicrotask(kick);
            kickTimer1 = setTimeout(kick, 50);
            kickTimer2 = setTimeout(kick, 400);
          }
        }
      } catch {
        if (!cancelled) {
          urlsRef.current = [];
          setIsAvailable(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      if (kickTimer1 !== undefined) clearTimeout(kickTimer1);
      if (kickTimer2 !== undefined) clearTimeout(kickTimer2);
    };
  }, [ensureAudio]);

  useEffect(() => {
    if (!isAvailable) return;
    const unlock = () => {
      const el = audioRef.current;
      if (pendingGestureUnmuteRef.current && el) {
        el.muted = false;
        pendingGestureUnmuteRef.current = false;
      }
      tryStartIfAllowedRef.current();
    };
    window.addEventListener('pointerdown', unlock, { capture: true, passive: true });
    window.addEventListener('keydown', unlock, { capture: true, passive: true });
    return () => {
      window.removeEventListener('pointerdown', unlock, { capture: true });
      window.removeEventListener('keydown', unlock, { capture: true });
    };
  }, [isAvailable]);

  useEffect(() => {
    if (!isAvailable) return;
    const onVis = () => {
      if (document.visibilityState === 'hidden') {
        const el = audioRef.current;
        if (el) {
          el.pause();
        }
      } else if (document.visibilityState === 'visible') {
        const el = audioRef.current;
        if (pendingGestureUnmuteRef.current && el) {
          el.muted = false;
          pendingGestureUnmuteRef.current = false;
        }
        tryStartIfAllowedRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [isAvailable]);

  useEffect(() => {
    if (!isAvailable) return;
    if (!bgmEnabled) {
      clearAutoUnmuteTimer();
      pendingGestureUnmuteRef.current = false;
      const el = audioRef.current;
      if (el) {
        el.muted = false;
      }
      el?.pause();
      setIsPlaying(false);
    } else {
      tryStartIfAllowed();
    }
  }, [bgmEnabled, isAvailable, tryStartIfAllowed, clearAutoUnmuteTimer]);

  const toggle = useCallback(() => {
    if (!isAvailable) return;
    if (pendingGestureUnmuteRef.current && audioRef.current) {
      clearAutoUnmuteTimer();
      audioRef.current.muted = false;
      pendingGestureUnmuteRef.current = false;
      return;
    }
    const next = !bgmEnabled;
    setBgmEnabled(next);
    setAppBgmEnabledLocal(next);
    bgmEnabledRef.current = next;
    if (!next) {
      clearAutoUnmuteTimer();
      pendingGestureUnmuteRef.current = false;
      const el = audioRef.current;
      if (el) {
        el.muted = false;
      }
      el?.pause();
      setIsPlaying(false);
    } else {
      playRandom();
    }
  }, [isAvailable, bgmEnabled, playRandom, clearAutoUnmuteTimer]);

  useEffect(
    () => () => {
      clearAutoUnmuteTimer();
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [clearAutoUnmuteTimer]
  );

  const value = useMemo(
    () => ({
      isPlaying,
      isAvailable,
      toggle,
    }),
    [isPlaying, isAvailable, toggle]
  );

  return (
    <AppBackgroundMusicContext.Provider value={value}>{children}</AppBackgroundMusicContext.Provider>
  );
};

export function useAppBackgroundMusic(): AppBackgroundMusicContextValue {
  const ctx = useContext(AppBackgroundMusicContext);
  if (!ctx) {
    throw new Error('useAppBackgroundMusic must be used within AppBackgroundMusicProvider');
  }
  return ctx;
}
